import { useId, useState } from 'react'
import { cn } from '@/lib/cn'

/**
 * Bitta seriyali ustun grafigi — inline SVG, bog'liqliksiz.
 *
 * NEGA KUTUBXONA EMAS: butun panelda hozir bitta grafik bor va u 7 nuqtali.
 * Recharts ≈95 KB gzip — bundle byudjeti 300 KB (X3), joriy 125 KB. Bitta
 * grafik uchun bu nomutanosib. A2 30+ widget'ga o'sganda qayta ko'riladi.
 *
 * Qoidalar (dataviz skill + grom-admin-design §9):
 *   · ustun grafik DOIM noldan boshlanadi — kesilgan o'q taqiqlanadi
 *   · qo'shni ustunlar orasida yuza oralig'i (quyida `GAP` — CHETLANISH izohi)
 *   · ma'lumot uchi yumaloq, asos chizig'iga tirkalgan
 *   · bitta seriya → LEGENDA KERAK EMAS (sarlavha seriyani nomlaydi)
 *   · matn rang tokenlarida, seriya rangida EMAS
 *   · har ustunda hover tooltip + `<title>` (ekran o'quvchi ham o'qiydi)
 *   · qiymatlar jadval ko'rinishida ham beriladi (kontrast/CVD relyefi)
 *   · o'q va to'r recessiv
 */

export interface BarDatum {
  /** X o'qi yorlig'i (qisqa). */
  label: string
  value: number
  /** Hover/`title` uchun to'liq matn (masalan to'liq sana). */
  title?: string
}

export function BarChart({
  data,
  emptyLabel,
  tableLabel,
  className,
}: {
  data: BarDatum[]
  emptyLabel: string
  tableLabel: string
  className?: string
}) {
  const uid = useId()
  const [hover, setHover] = useState<number | null>(null)

  if (!data.length || data.every((d) => d.value === 0)) {
    return (
      <p className={cn('py-8 text-center text-sm text-tertiary', className)}>{emptyLabel}</p>
    )
  }

  const max = Math.max(...data.map((d) => d.value))
  const H = 120 // chizma balandligi (px)

  /**
   * ⚠️ CHETLANISH (dataviz «2px yuza oralig'i»).
   *
   * `viewBox` eni 100 birlik va `preserveAspectRatio="none"` — ya'ni X o'qi
   * konteyner eniga cho'ziladi. ~880px konteynerda 1 birlik ≈ 8.8px, demak
   * `GAP=2` ekranda ≈18px bo'lib chiqadi, 2px emas.
   *
   * Bu ATAYLAB qoldirildi: 2px oraliq 7 ta kunlik ustunni gistogrammaga
   * o'xshatib qo'yadi (ustunlar deyarli tegib turadi). Spetsifikatsiyadagi
   * 2px zich ko'p-seriyali/stacked holat uchun. Bu yerda 7 ta diskret kunlik
   * chelak bor — ular alohida o'qilishi kerak.
   *
   * Agar keyinchalik oraliq HAQIQIY piksel bo'lishi kerak bo'lsa: SVG o'rniga
   * flex `div` lar ishlating — u holda `gap-[2px]` to'g'ridan-to'g'ri ishlaydi.
   */
  const GAP = 2
  const slot = 100 / data.length // foizda

  return (
    <div className={className}>
      <div className="relative" style={{ height: H + 20 }}>
        {/* Recessiv asos chizig'i — nol darajasi ko'rinib tursin */}
        <div
          className="absolute right-0 left-0 border-t border-border-subtle"
          style={{ top: H }}
          aria-hidden
        />

        <svg
          width="100%"
          height={H}
          viewBox={`0 0 100 ${H}`}
          preserveAspectRatio="none"
          role="img"
          aria-labelledby={`${uid}-t`}
          className="block overflow-visible"
        >
          <title id={`${uid}-t`}>{tableLabel}</title>
          {data.map((d, i) => {
            // Noldan boshlanadi: balandlik qiymatga to'g'ridan-to'g'ri proporsional.
            const h = max > 0 ? (d.value / max) * (H - 4) : 0
            const x = i * slot + GAP / 2
            const w = slot - GAP
            return (
              <rect
                key={d.label}
                x={x}
                y={H - h}
                width={w}
                height={Math.max(h, d.value > 0 ? 2 : 0)}
                rx="1.5"
                fill="var(--chart-1)"
                opacity={hover === null || hover === i ? 1 : 0.45}
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover(null)}
                style={{ transition: 'opacity 120ms' }}
              >
                <title>{`${d.title ?? d.label}: ${d.value.toLocaleString()}`}</title>
              </rect>
            )
          })}
        </svg>

        {/* X o'qi yorliqlari — SVG dan tashqarida, shunda cho'zilmaydi */}
        <div className="absolute right-0 left-0 flex" style={{ top: H + 4 }}>
          {data.map((d, i) => (
            <span
              key={d.label}
              className={cn(
                'text-center text-xs tabular',
                hover === i ? 'text-primary' : 'text-tertiary',
              )}
              style={{ width: `${slot}%` }}
            >
              {d.label}
            </span>
          ))}
        </div>
      </div>

      {/* Kontrast WARN relyefi: qiymatlar rangdan tashqari matn bilan ham
          beriladi — rang yolg'iz signal bo'lib qolmaydi (skill §2). */}
      {/* `table-fixed` MAJBURIY: usiz ustun kengligi matnga qarab hisoblanadi
          («142» «0» dan keng bo'ladi) va raqamlar o'z ustunidan siljib ketadi. */}
      <table className="mt-3 w-full table-fixed border-collapse text-sm">
        <caption className="sr-only">{tableLabel}</caption>
        <tbody>
          <tr>
            {data.map((d, i) => (
              <td
                key={d.label}
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover(null)}
                className={cn(
                  'border-t border-border-subtle px-1 py-1 text-center font-mono text-xs tabular',
                  hover === i ? 'text-primary' : 'text-secondary',
                )}
              >
                {d.value.toLocaleString()}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  )
}
